
import axios from 'axios';
import { supabase } from '@/config/supabase';

// Smart API URL selection:
// - Development: Use Vite proxy at /api (bypasses CORS)
// - Production: Use VITE_API_URL from environment or fallback
const API_URL = import.meta.env.DEV 
  ? '/api'  // Development: use Vite proxy to avoid CORS
  : (import.meta.env.VITE_API_URL || 'https://majengo-render.onrender.com/api');

const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 60000, // 60 seconds timeout
  timeoutErrorMessage: 'Request timeout - server not responding',
});

// Cache the token in memory to avoid calling getSession() on every request,
// which causes significant overhead and delays.
let currentToken: string | null = null;

// Initialize token on load
supabase.auth.getSession().then(({ data: { session } }) => {
  currentToken = session?.access_token || null;
});

// Update token automatically when auth state changes (login, logout, token refresh)
supabase.auth.onAuthStateChange((_event, session) => {
  currentToken = session?.access_token || null;
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    // Only log in dev to reduce console spam
    if (import.meta.env.DEV) {
      console.log(`[apiClient] REQUEST: ${config.method?.toUpperCase()} ${config.url}`);
    }
    
    if (currentToken) {
      config.headers.Authorization = `Bearer ${currentToken}`;
    }

    return config;
  },
  (error) => {
    console.error('[apiClient] Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
apiClient.interceptors.response.use(
  (response) => {
    if (import.meta.env.DEV) {
      console.log(`[apiClient] RESPONSE: ${response.status} ${response.config.url}`);
    }
    return response;
  },
  (error) => {
    const url = error.config?.url || 'unknown';
    const status = error.response?.status || 'no response';
    console.error(`[apiClient] ERROR: ${status} ${url}`, error.message);

    // Handle specific error cases
    if (error.code === 'ECONNABORTED') {
      console.error('[apiClient] Request timeout - server not responding');
    }

    if (error.response?.status === 401) {
      console.warn('[apiClient] Unauthorized - user might need to login');
    }

    if (error.response?.status === 500) {
      console.error('[apiClient] Server error:', error.response.data);
    }

    if (!error.response) {
      console.error('[apiClient] Network error - cannot reach server');
    }

    return Promise.reject(error);
  }
);

console.log('[apiClient] Using API_URL:', API_URL);
export default apiClient;